/**
 * Holds a training session in "connecting" until the AI customer is heard.
 *
 * The socket opens and session_started arrives 3-4 seconds before the
 * customer's first word. Showing the session as live in that gap left the rep
 * looking at an End button with nothing happening, so both training pages go
 * live on the first AUDIBLE customer audio (or, as a fallback, the first
 * customer transcript text). If the customer never speaks, the session is
 * failed after CUSTOMER_VOICE_TIMEOUT_MS instead of hanging on a spinner.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { hasAudibleVoice } from '../utils/customerVoice';

export const CUSTOMER_VOICE_TIMEOUT_MS = 20000;

interface FirstCustomerVoiceOptions {
  /** Called once, on the first sign the customer is speaking. */
  onVoice: () => void;
  /** Called when the customer has not spoken within the timeout. */
  onTimeout: () => void;
  timeoutMs?: number;
}

export interface FirstCustomerVoice {
  /** Start waiting (call when the session starts connecting). */
  arm: () => void;
  /** Stop waiting without firing anything (teardown, failure, end). */
  disarm: () => void;
  noteCustomerAudio: (pcm: ArrayBuffer) => void;
  noteCustomerText: (text: string) => void;
}

export function useFirstCustomerVoice({
  onVoice,
  onTimeout,
  timeoutMs = CUSTOMER_VOICE_TIMEOUT_MS,
}: FirstCustomerVoiceOptions): FirstCustomerVoice {
  const isWaitingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest callbacks, so socket handlers registered once still see them.
  const onVoiceRef = useRef(onVoice);
  const onTimeoutRef = useRef(onTimeout);
  onVoiceRef.current = onVoice;
  onTimeoutRef.current = onTimeout;

  const disarm = useCallback(() => {
    isWaitingRef.current = false;
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const arm = useCallback(() => {
    disarm();
    isWaitingRef.current = true;
    timerRef.current = setTimeout(() => {
      if (!isWaitingRef.current) return;
      disarm();
      onTimeoutRef.current();
    }, timeoutMs);
  }, [disarm, timeoutMs]);

  const fireVoice = useCallback(() => {
    if (!isWaitingRef.current) return;
    disarm();
    onVoiceRef.current();
  }, [disarm]);

  const noteCustomerAudio = useCallback(
    (pcm: ArrayBuffer) => {
      if (isWaitingRef.current && hasAudibleVoice(pcm)) fireVoice();
    },
    [fireVoice]
  );

  const noteCustomerText = useCallback(
    (text: string) => {
      if (isWaitingRef.current && text.trim()) fireVoice();
    },
    [fireVoice]
  );

  useEffect(() => disarm, [disarm]);

  // Stable identity: callers put this in effect/callback dependencies, and a
  // fresh object per render would re-run their teardown effects every render.
  return useMemo(
    () => ({ arm, disarm, noteCustomerAudio, noteCustomerText }),
    [arm, disarm, noteCustomerAudio, noteCustomerText]
  );
}
